import { type ReactNode } from "react";

/**
 * The cold-core "Waking the control plane" banner (design 12.3; NFR-7 AC1).
 *
 * Shown when an API call returns the BFF's 503 `waking` state (classified by `lib/api.ts`). The
 * shell itself is served by the asset router and stays up regardless; this banner just tells the
 * operator the data is on its way and offers a manual retry. TanStack Query already retries a waking
 * call with a backoff, so the button is a courtesy for an operator who does not want to wait.
 *
 * It is a polite live region (text, not colour alone) with an actionable retry (design 12.4).
 */
export function WakingBanner(props: {
  /** Called when the operator asks to retry now (e.g. refetch the active queries). */
  onRetry: () => void;
  /** Whether a retry is currently in flight, to disable the button and reflect progress. */
  retrying?: boolean;
}): ReactNode {
  return (
    <div className="waking" role="status" aria-live="polite">
      <span aria-hidden="true">◐</span>
      <span className="waking__text">
        <strong>Waking the control plane.</strong> The core is starting or briefly unavailable. The
        view will fill in automatically once it responds.
      </span>
      <button
        type="button"
        className="control__button"
        onClick={props.onRetry}
        disabled={props.retrying === true}
      >
        {props.retrying === true ? "Retrying…" : "Retry now"}
      </button>
    </div>
  );
}
